import { useState, useEffect, useContext } from 'react';
import { UserContext } from '@/UserContext';
import { canDisplayUserFeedback, getTenantCloudType } from '@ms/inapp-feedback-loader';
import {
  FeedbackAuthenticationType,
  TenantCloudType,
  IFeedbackInitOptions,
} from '@ms/ocv-inapp-feedback-types';
import { acquireToken } from '@/auth/tokenProvider';
import { logFeedbackPolicyCheck } from '@/telemetry/telemetryEvents';
import { FEEDBACK_APP_ID, FEEDBACK_IS_PRODUCTION, FEEDBACK_CPS_CALLER_HINT } from '@/constants/feedback';

export interface FeedbackPolicyCheckResult {
  isAllowed: boolean;
  isLoading: boolean;
  cloudType?: TenantCloudType;
  initOptions?: IFeedbackInitOptions;
  error?: string;
}

/**
 * Shared hook to check CPS policies for feedback
 * Used by Footer (to show/hide link) and FeedbackForm (to validate before launching SDK)
 */
export function useFeedbackPolicyCheck(source: 'footer' | 'feedback-form'): FeedbackPolicyCheckResult {
  const user = useContext(UserContext);
  const [isAllowed, setIsAllowed] = useState(true); // Default to true for anonymous users
  const [isLoading, setIsLoading] = useState(false);
  const [cloudType, setCloudType] = useState<TenantCloudType | undefined>();
  const [initOptions, setInitOptions] = useState<IFeedbackInitOptions | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    // Skip policy check if no user (anonymous) or if user lacks required fields
    if (!user?.tenantId || !user?.localAccountId) {
      console.log(`[useFeedbackPolicyCheck:${source}] No authenticated user, feedback allowed (anonymous fallback)`);
      setIsAllowed(true);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    const checkFeedbackPolicies = async () => {
      const startTime = Date.now();

      try {
        console.log(`[useFeedbackPolicyCheck:${source}] Checking CPS policies for user:`, user.tenantId);

        // Step 1: Resolve tenant cloud type
        const cloudTypeResult = await getTenantCloudType({
          appId: FEEDBACK_APP_ID,
          tenantId: user.tenantId,
          authenticationType: FeedbackAuthenticationType.AAD,
          userId: `a:${user.localAccountId}`,
          isProduction: FEEDBACK_IS_PRODUCTION,
        });

        if (!isMounted) return;

        const resolvedCloudType = cloudTypeResult ?? TenantCloudType.Worldwide;
        setCloudType(resolvedCloudType);

        // Step 2: Build minimal feedbackOptions for policy check
        const feedbackOptions = {
          appId: FEEDBACK_APP_ID,
          authenticationType: FeedbackAuthenticationType.AAD as const,
          isProduction: FEEDBACK_IS_PRODUCTION,
          tenantId: user.tenantId,
          userId: `a:${user.localAccountId}`,
          cloudPolicyServiceConfig: {
            callerAppHint: FEEDBACK_CPS_CALLER_HINT,
          },
          feedbackConfig: {
            scenarioConfig: {
              isScenarioEnabled: false,
            },
            complianceChecks: {
              tenantCloudType: resolvedCloudType,
            },
          },
          callbackFunctions: {
            getAccessToken: async (scopes: string | string[]) => {
              const scopeArray = Array.isArray(scopes) ? scopes : [scopes];
              const token = await acquireToken(scopeArray);
              return { token };
            },
          },
        };

        // Step 3: Check if feedback is allowed and get init options
        const { canDisplay, init } = await canDisplayUserFeedback(feedbackOptions);

        if (!isMounted) return;

        const durationMs = Date.now() - startTime;
        console.log(`[useFeedbackPolicyCheck:${source}] CPS policy check result:`, { canDisplay });

        // Log policy check result
        logFeedbackPolicyCheck(source, canDisplay ? 'allowed' : 'blocked', {
          cloudType: resolvedCloudType,
          durationMs,
          tenantId: user.tenantId,
        });

        setIsAllowed(canDisplay);
        setInitOptions(init);
        setIsLoading(false);
      } catch (err) {
        console.error(`[useFeedbackPolicyCheck:${source}] Error checking feedback policies:`, err);
        if (!isMounted) return;

        const durationMs = Date.now() - startTime;
        const errorMessage = err instanceof Error ? err.message : String(err);

        // Log policy check error
        logFeedbackPolicyCheck(source, 'error', {
          errorMessage,
          durationMs,
          tenantId: user.tenantId,
        });

        // On error, hide feedback to be safe
        setIsAllowed(false);
        setIsLoading(false);
        setError(errorMessage);
      }
    };

    checkFeedbackPolicies();

    return () => {
      isMounted = false;
    };
  }, [user?.tenantId, user?.localAccountId, source]);

  return { isAllowed, isLoading, cloudType, initOptions, error };
}
